export type QuizSlot = 'pre' | 'post';
export type QuizMode = 'bank' | 'level-rules';

export interface LevelRule {
  level: 1 | 2 | 3;
  questionCount: number;
}

export interface BankQuestionOption {
  id: string;
  text: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  topicIndex: number;
}

export interface TopicQuizConfig {
  slot: QuizSlot;
  mode: QuizMode;
  selectedQuestionIds?: string[];
  levelRules?: LevelRule[];
}

export interface SubtopicItem {
  id: string;
  index: number;
  title: string;
  durationMinutes: number;
  preQuiz?: TopicQuizConfig;
  postQuiz?: TopicQuizConfig;
}

export interface TimelineTopicItem {
  id: string;
  index: number;
  title: string;
  durationMinutes: number;
  description: string;
  preQuiz?: TopicQuizConfig;
  postQuiz?: TopicQuizConfig;
  /** Workshop only — which day this topic belongs to (0-based). */
  workshopDayIndex?: number;
  /** Optional subtopics — when present, topic durationMinutes is the sum. */
  subtopics?: SubtopicItem[];
}

/** Fixed question count per quiz slot (finalized structure). */
export const QUIZ_FIXED_QUESTION_COUNT: Record<QuizSlot, number> = {
  pre: 2,
  post: 5,
};

/** Pre-quiz is optional; post-quiz is mandatory for every topic. */
export const QUIZ_SLOT_POLICY: Record<
  QuizSlot,
  { required: boolean; label: string; description: string }
> = {
  pre: {
    required: false,
    label: 'Optional',
    description: 'Warm-up check before the topic — add only if the trainer wants it.',
  },
  post: {
    required: true,
    label: 'Required',
    description: 'Assessment after the topic — auto-configured with level rules.',
  },
};

export function isQuizSlotOptional(slot: QuizSlot): boolean {
  return !QUIZ_SLOT_POLICY[slot].required;
}

export function isQuizSlotMandatory(slot: QuizSlot): boolean {
  return QUIZ_SLOT_POLICY[slot].required;
}

export function getQuizSlotPolicyLabel(slot: QuizSlot): string {
  return QUIZ_SLOT_POLICY[slot].label;
}

export function getQuizSlotPolicyDescription(slot: QuizSlot): string {
  return QUIZ_SLOT_POLICY[slot].description;
}

export function hasConfiguredPostQuiz(topic: TimelineTopicItem): boolean {
  return Boolean(topic.postQuiz);
}

export function isPostQuizComplete(topic: TimelineTopicItem): boolean {
  return topic.postQuiz ? isQuizQuestionCountComplete(topic.postQuiz) : false;
}

export function isPreQuizComplete(topic: TimelineTopicItem): boolean {
  if (!topic.preQuiz) return true;
  return isQuizQuestionCountComplete(topic.preQuiz);
}

export type TopicQuizValidationIssueType =
  | 'missing_post_quiz'
  | 'incomplete_post_quiz'
  | 'incomplete_pre_quiz';

export interface TopicQuizValidationIssue {
  topicId: string;
  topicTitle: string;
  type: TopicQuizValidationIssueType;
  message: string;
}

export function validateTopicQuizPlan(topics: TimelineTopicItem[]): TopicQuizValidationIssue[] {
  const issues: TopicQuizValidationIssue[] = [];

  for (const topic of topics) {
    if (topic.subtopics?.length) {
      for (const sub of topic.subtopics) {
        const label = `${topic.title} > ${sub.title}`;
        if (!sub.postQuiz) {
          issues.push({
            topicId: sub.id,
            topicTitle: label,
            type: 'missing_post_quiz',
            message: `Post-quiz is required for "${label}".`,
          });
          continue;
        }
        if (!isQuizQuestionCountComplete(sub.postQuiz)) {
          issues.push({
            topicId: sub.id,
            topicTitle: label,
            type: 'incomplete_post_quiz',
            message: `Post-quiz for "${label}" needs ${getQuizFixedQuestionCount('post')} questions.`,
          });
        }
        if (sub.preQuiz && !isQuizQuestionCountComplete(sub.preQuiz)) {
          issues.push({
            topicId: sub.id,
            topicTitle: label,
            type: 'incomplete_pre_quiz',
            message: `Pre-quiz for "${label}" is incomplete — finish it or remove it.`,
          });
        }
      }
    } else {
      if (!topic.postQuiz) {
        issues.push({
          topicId: topic.id,
          topicTitle: topic.title,
          type: 'missing_post_quiz',
          message: `Post-quiz is required for "${topic.title}".`,
        });
        continue;
      }
      if (!isPostQuizComplete(topic)) {
        issues.push({
          topicId: topic.id,
          topicTitle: topic.title,
          type: 'incomplete_post_quiz',
          message: `Post-quiz for "${topic.title}" needs ${getQuizFixedQuestionCount('post')} questions.`,
        });
      }
      if (topic.preQuiz && !isPreQuizComplete(topic)) {
        issues.push({
          topicId: topic.id,
          topicTitle: topic.title,
          type: 'incomplete_pre_quiz',
          message: `Pre-quiz for "${topic.title}" is incomplete — finish it or remove it.`,
        });
      }
    }
  }

  return issues;
}

export function isTopicQuizPlanValid(topics: TimelineTopicItem[]): boolean {
  return validateTopicQuizPlan(topics).length === 0;
}

export function canRemoveQuizSlot(slot: QuizSlot): boolean {
  return isQuizSlotOptional(slot);
}

/** Max total quiz time by slot (derived from typical level mix). */
export const QUIZ_MAX_DURATION_SECONDS: Record<QuizSlot, number> = {
  pre: 2 * 60,
  post: 5 * 60,
};

/** L1 = 20s, L2 = 40s, L3 = 60s per question. */
export const QUIZ_SECONDS_BY_LEVEL: Record<1 | 2 | 3, number> = {
  1: 20,
  2: 40,
  3: 60,
};

export function getQuizFixedQuestionCount(slot: QuizSlot): number {
  return QUIZ_FIXED_QUESTION_COUNT[slot];
}

export function isQuizQuestionCountComplete(quiz: TopicQuizConfig): boolean {
  return getQuizQuestionCount(quiz) === getQuizFixedQuestionCount(quiz.slot);
}

export function getQuizMaxDurationSeconds(slot: QuizSlot): number {
  return QUIZ_MAX_DURATION_SECONDS[slot];
}

export function formatQuizMaxDuration(slot: QuizSlot): string {
  return `${getQuizFixedQuestionCount(slot)} questions`;
}

/** Easy → L1, Medium → L2, Hard → L3. */
export function difficultyToLevel(difficulty: BankQuestionOption['difficulty']): 1 | 2 | 3 {
  if (difficulty === 'Easy') return 1;
  if (difficulty === 'Medium') return 2;
  return 3;
}

export function formatLevelTimeLabel(level: 1 | 2 | 3): string {
  return `L${level} · ${QUIZ_SECONDS_BY_LEVEL[level]}s`;
}

export function formatQuizDurationSeconds(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function distributeLevelRules(total: number): LevelRule[] {
  const l1 = Math.ceil(total / 3);
  const l2 = Math.ceil((total - l1) / 2);
  const l3 = Math.max(0, total - l1 - l2);
  return [
    { level: 1, questionCount: l1 },
    { level: 2, questionCount: l2 },
    { level: 3, questionCount: l3 },
  ];
}

export function createEmptyQuiz(slot: QuizSlot): TopicQuizConfig {
  return createDefaultQuiz(slot);
}

export function createDefaultQuiz(slot: QuizSlot): TopicQuizConfig {
  return {
    slot,
    mode: 'level-rules',
    levelRules: distributeLevelRules(getQuizFixedQuestionCount(slot)),
  };
}

export function applyDefaultQuizzesToTopic(topic: TimelineTopicItem): TimelineTopicItem {
  const next = { ...topic };
  if (next.subtopics?.length) {
    next.subtopics = next.subtopics.map((sub) => {
      const s = { ...sub };
      if (!s.postQuiz || !isQuizQuestionCountComplete(s.postQuiz)) {
        s.postQuiz = createDefaultQuiz('post');
      }
      if (s.preQuiz && !isQuizQuestionCountComplete(s.preQuiz)) {
        s.preQuiz = createDefaultQuiz('pre');
      }
      return s;
    });
    delete next.preQuiz;
    delete next.postQuiz;
  } else {
    if (!next.postQuiz || !isQuizQuestionCountComplete(next.postQuiz)) {
      next.postQuiz = createDefaultQuiz('post');
    }
    if (next.preQuiz && !isQuizQuestionCountComplete(next.preQuiz)) {
      next.preQuiz = createDefaultQuiz('pre');
    }
  }
  return next;
}

export function normalizeTopicQuizPlan(topics: TimelineTopicItem[]): TimelineTopicItem[] {
  return topics.map(applyDefaultQuizzesToTopic);
}

export function getQuizDurationSecondsUncapped(
  quiz: TopicQuizConfig,
  bankOptions: BankQuestionOption[],
): number {
  if (quiz.mode === 'bank') {
    const selected = new Set(quiz.selectedQuestionIds ?? []);
    return bankOptions
      .filter((q) => selected.has(q.id))
      .reduce((sum, q) => sum + QUIZ_SECONDS_BY_LEVEL[difficultyToLevel(q.difficulty)], 0);
  }
  return (quiz.levelRules ?? []).reduce(
    (sum, r) => sum + r.questionCount * QUIZ_SECONDS_BY_LEVEL[r.level],
    0,
  );
}

export function normalizeQuizConfig(
  quiz: TopicQuizConfig,
  _bankOptions: BankQuestionOption[],
): TopicQuizConfig {
  const target = getQuizFixedQuestionCount(quiz.slot);
  if (quiz.mode === 'bank' && quiz.selectedQuestionIds) {
    return { ...quiz, selectedQuestionIds: quiz.selectedQuestionIds.slice(0, target) };
  }
  if (quiz.mode === 'level-rules' && quiz.levelRules) {
    const levelRules = quiz.levelRules.map((r) => ({ ...r }));
    let total = () => levelRules.reduce((s, r) => s + r.questionCount, 0);
    while (total() > target) {
      const rule =
        [...levelRules].reverse().find((r) => r.questionCount > 0) ??
        levelRules.find((r) => r.questionCount > 0);
      if (!rule) break;
      rule.questionCount -= 1;
    }
    return { ...quiz, levelRules };
  }
  return quiz;
}

export function canAddBankQuestion(
  selectedIds: string[],
  questionId: string,
  slot: QuizSlot,
  _bankOptions: BankQuestionOption[],
): boolean {
  if (selectedIds.includes(questionId)) return true;
  return selectedIds.length < getQuizFixedQuestionCount(slot);
}

export function isQuizWithinTimeLimit(
  quiz: TopicQuizConfig,
  _bankOptions: BankQuestionOption[],
): boolean {
  return isQuizQuestionCountComplete(quiz);
}

export function getMaxLevelQuestionCount(
  level: 1 | 2 | 3,
  config: TopicQuizConfig,
): number {
  const target = getQuizFixedQuestionCount(config.slot);
  const otherCount = (config.levelRules ?? []).reduce(
    (sum, r) => (r.level === level ? sum : sum + r.questionCount),
    0,
  );
  return Math.max(0, target - otherCount);
}

export interface SessionDurationBreakdown {
  topicMinutes: number;
  quizMinutes: number;
  totalMinutes: number;
}

export function getQuizQuestionCount(quiz: TopicQuizConfig): number {
  if (quiz.mode === 'bank') {
    return quiz.selectedQuestionIds?.length ?? 0;
  }
  return quiz.levelRules?.reduce((sum, r) => sum + r.questionCount, 0) ?? 0;
}

export function getQuizDurationSeconds(
  quiz: TopicQuizConfig,
  bankOptions: BankQuestionOption[],
): number {
  if (getQuizQuestionCount(quiz) === 0) return 0;
  return Math.min(
    getQuizDurationSecondsUncapped(quiz, bankOptions),
    getQuizMaxDurationSeconds(quiz.slot),
  );
}

/** Planner/session totals use whole minutes (rounded up). */
export function getQuizDurationMinutes(
  quiz: TopicQuizConfig,
  bankOptions: BankQuestionOption[],
): number {
  const seconds = getQuizDurationSeconds(quiz, bankOptions);
  return seconds === 0 ? 0 : Math.ceil(seconds / 60);
}

export function getSubtopicQuizMinutes(
  sub: SubtopicItem,
  bankOptions: BankQuestionOption[],
): number {
  let q = 0;
  if (sub.preQuiz) q += getQuizDurationMinutes(sub.preQuiz, bankOptions);
  if (sub.postQuiz) q += getQuizDurationMinutes(sub.postQuiz, bankOptions);
  return q;
}

export function getTopicBlockDuration(
  topic: TimelineTopicItem,
  bankOptions: BankQuestionOption[],
): number {
  let total = topic.durationMinutes;
  if (topic.subtopics?.length) {
    for (const sub of topic.subtopics) {
      total += getSubtopicQuizMinutes(sub, bankOptions);
    }
  } else {
    if (topic.preQuiz) total += getQuizDurationMinutes(topic.preQuiz, bankOptions);
    if (topic.postQuiz) total += getQuizDurationMinutes(topic.postQuiz, bankOptions);
  }
  return total;
}

export function calcSessionDuration(
  topics: TimelineTopicItem[],
  bankOptions: BankQuestionOption[],
): SessionDurationBreakdown {
  let topicMinutes = 0;
  let quizMinutes = 0;

  for (const topic of topics) {
    topicMinutes += topic.durationMinutes > 0 ? topic.durationMinutes : 0;
    if (topic.subtopics?.length) {
      for (const sub of topic.subtopics) {
        quizMinutes += getSubtopicQuizMinutes(sub, bankOptions);
      }
    } else {
      if (topic.preQuiz) quizMinutes += getQuizDurationMinutes(topic.preQuiz, bankOptions);
      if (topic.postQuiz) quizMinutes += getQuizDurationMinutes(topic.postQuiz, bankOptions);
    }
  }

  return {
    topicMinutes,
    quizMinutes,
    totalMinutes: topicMinutes + quizMinutes,
  };
}

export function formatDurationMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function getQuizSummary(quiz: TopicQuizConfig, bankOptions: BankQuestionOption[] = []): string {
  const count = getQuizQuestionCount(quiz);
  const target = getQuizFixedQuestionCount(quiz.slot);
  const seconds = getQuizDurationSeconds(
    { ...quiz, mode: 'level-rules' },
    bankOptions,
  );
  const timeLabel = seconds > 0 ? ` · ~${formatQuizDurationSeconds(seconds)}` : '';
  const rules = quiz.levelRules ?? [];
  const parts = rules
    .filter((r) => r.questionCount > 0)
    .map((r) => `L${r.level}:${r.questionCount}`);
  return `Level Rules · ${parts.join(' ') || 'unset'} (${count}/${target} questions${timeLabel})`;
}
